import { createStore } from "vuex";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  updatePassword,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getDatabase,
  ref,
  set,
  push,
  update,
  get,
  onValue,
  remove,
} from "firebase/database";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";

interface BlacklistItem {
  code: string;
  cone: number;
  created_at: string;
  description: string;
  entry: string;
  family: string;
  foot: string;
  levelName: string;
  metadata: string;
  method: string;
  name: string;
  side: string;
  version: string;
  permanentlyHidden: boolean;
}

// Promesa que resuelve una sola vez cuando Firebase confirma el estado inicial de auth.
// main.ts la awaita antes de montar la app, eliminando la race condition en el router guard.
let _resolveAuthReady: () => void;
export const authReady = new Promise<void>((resolve) => {
  _resolveAuthReady = resolve;
});

// Almacena el unsubscribe del listener de sesión activa para limpiarlo en logout
let _unsubscribeSessionListener: (() => void) | null = null;

const store = createStore({
  state: {
    user: {
      loggedIn: false,
      data: null,
      uid: "",
    },
    combos: 0,
    myCombos: [],
    combosLoaded: false,
    tricks: [],
    tricksLoaded: false,
    figures: [],
    figuresLoaded: false,
    blacklist: [],
    blacklistLoaded: false,
    blacklistLoadedAt: 0,
    myLab: [],
    labLoaded: false,
    lab: 0,
    codes: null,
    level: null,
    role: "",
    userList: [],
    userDetail: {},
    isActive: null,
    kickedOut: false,
  },
  getters: {
    getUser(state) {
      return state.user;
    },
    getCombos(state) {
      return state.combos;
    },
    getBlacklist(state) {
      return state.blacklist;
    },
    getTricks(state) {
      return state.tricks;
    },
    getFigures(state) {
      return state.figures;
    },
    getMyCombos(state) {
      return state.myCombos;
    },
    getLevel(state) {
      return state.level;
    },
    getRole(state) {
      return state.role;
    },
    getUid(state) {
      return state.user.uid;
    },
    getUserList(state) {
      return state.userList;
    },
    getUserDetail(state) {
      return state.userDetail;
    },
    getMyLab(state) {
      return state.myLab;
    },
  },
  mutations: {
    SET_LOGGED_IN(state, value) {
      state.user.loggedIn = value;
    },
    SET_USER(state, data) {
      state.user.data = data;
      if (data) {
        state.user.uid = data.uid;
      }
    },
    SET_COMBOS(state, data) {
      state.combos = data;
    },
    SET_MY_COMBOS(state, data) {
      state.myCombos = data;
    },
    SET_COMBOS_LOADED(state, value: boolean) {
      state.combosLoaded = value;
    },
    SET_TRICKS_LOADED(state, value: boolean) {
      state.tricksLoaded = value;
    },
    SET_FIGURES_LOADED(state, value: boolean) {
      state.figuresLoaded = value;
    },
    SET_BLACKLIST_LOADED(state, value: boolean) {
      state.blacklistLoaded = value;
      state.blacklistLoadedAt = value ? Date.now() : 0;
    },
    SET_LAB_LOADED(state, value: boolean) {
      state.labLoaded = value;
    },
    SET_BLACKLIST(state, data) {
      state.blacklist = data;
    },
    SET_TRICKS(state, data) {
      state.tricks = data;
    },
    SET_FIGURES(state, data) {
      state.figures = data;
    },
    SET_LEVEL(state, data) {
      state.level = data;
    },
    SET_ROLE(state, data) {
      state.role = data;
    },
    SET_CODES(state, data) {
      state.codes = data;
    },
    SET_USERS_DATA(state, data) {
      state.userList = data;
    },
    SET_USER_DETAIL(state, data) {
      state.userDetail = data;
    },
    SET_IS_ACTIVE(state, data) {
      state.isActive = data;
    },
    SET_KICKED_OUT(state, value: boolean) {
      state.kickedOut = value;
    },
    SET_LAB(state, data) {
      state.lab = data;
    },
    SET_MY_LAB(state, data) {
      state.myLab = data;
    },
  },
  actions: {
    initAuthState(context) {
      const auth = getAuth();
      let firstCall = true;
      onAuthStateChanged(auth, (user) => {
        if (user) {
          context.commit("SET_LOGGED_IN", true);
          context.commit("SET_USER", user);

          // Carga perfil en segundo plano sin bloquear el mount de la app
          context.dispatch("loadUserProfile", user.uid);

          // Arranca listener de sesión única: si el sessionId en RTDB cambia
          // (otro dispositivo hizo login), fuerza el logout en este dispositivo
          const db = getDatabase();
          const sessionRef = ref(db, `users/${user.uid}/sessionId`);
          _unsubscribeSessionListener = onValue(sessionRef, (snapshot) => {
            const remoteSessionId = snapshot.val();
            const localSessionId = localStorage.getItem("sessionId");
            if (localSessionId && remoteSessionId && remoteSessionId !== localSessionId) {
              context.commit("SET_KICKED_OUT", true);
              context.dispatch("logOut");
            }
          });
        } else {
          // Limpia el listener cuando el usuario cierra sesión
          if (_unsubscribeSessionListener) {
            _unsubscribeSessionListener();
            _unsubscribeSessionListener = null;
          }
          context.commit("SET_LOGGED_IN", false);
          context.commit("SET_USER", null);
        }
        if (firstCall) {
          firstCall = false;
          _resolveAuthReady();
        }
      });
    },

    async register(context, { email, password, name }) {
      const response = await createUserWithEmailAndPassword(
        getAuth(),
        email,
        password
      );
      if (!response) throw new Error("Unable to register user");

      // Ambas escrituras en paralelo — el perfil RTDB debe existir antes de retornar
      await Promise.all([
        updateProfile(response.user, { displayName: name }),
        set(ref(getDatabase(), "users/" + response.user.uid), {
          level: 0,
          role: "user",
          isActive: true,
          name: name,
        }),
      ]);

      context.commit("SET_USER", response.user);
    },

    async updatePassword(context, { newPassword }) {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user !== null) {
        try {
          await updatePassword(user, newPassword);
          // Puedes redirigir al usuario a otra página o mostrar un mensaje de éxito aquí
        } catch (error) {
          console.error("Error al cambiar la contraseña:", error.message);
          // Maneja el error de manera adecuada, por ejemplo, mostrando un mensaje de error al usuario
        }
      }
    },

    async getUsersList(context) {
      const db = getDatabase();
      const snapshot = await get(ref(db, "users/"));
      const data = snapshot.val();
      if (!data) {
        context.commit("SET_USERS_DATA", []);
        return;
      }
      const userList = Object.entries(data)
        .filter(([, u]: any) => u.role !== "admin")
        .map(([uid, u]: any) => ({ uid, name: u.name }))
        .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
      context.commit("SET_USERS_DATA", userList);
    },
    async getUserData(context, uid) {
      const db = getDatabase();
      const usersRef = ref(db, "users/" + uid);

      const snapshot = await get(usersRef);
      const data = snapshot.val();
      data.level = data.level.toString();
      if (!data) {
        return [];
      } else {
        context.commit("SET_USER_DETAIL", data);
        return data;
      }
    },

    async updateUserDataByAdmin(context, { uid, level, isActive }) {
      const db = getDatabase();
      return update(ref(db, "users/" + uid), {
        level: level,
        isActive: isActive,
      })
        .then(() => {
          return true;
        })
        .catch((err) => {
          console.error(err);
          return err;
        });
    },

    async logIn(context, { email, password }) {
      await signInWithEmailAndPassword(getAuth(), email, password)
        .then(async (data) => {
          context.commit("SET_USER", data.user);
          context.commit("SET_LOGGED_IN", true);
          context.commit("SET_KICKED_OUT", false);

          // Sesión única: escribe un sessionId nuevo en localStorage y RTDB.
          // Cualquier otro dispositivo con sesión abierta detectará el cambio y hará logout.
          const sessionId = uuidv4();
          localStorage.setItem("sessionId", sessionId);
          const db = getDatabase();
          await set(ref(db, `users/${data.user.uid}/sessionId`), sessionId);

          await context.dispatch("loadUserProfile", data.user.uid);
        })
        .catch((error) => {
          switch (error.code) {
            case "auth/invalid-email":
              throw new Error("Invalid email");
            case "auth/user-not-found":
              throw new Error("No account with that email was found");
            case "auth/wrong-password":
              throw new Error("Incorrect password");
            default:
              throw new Error("Email or password was incorrect");
          }
        });
    },

    async logOut(context) {
      // Desuscribe el listener ANTES de cualquier operación async para evitar re-entrada
      if (_unsubscribeSessionListener) {
        _unsubscribeSessionListener();
        _unsubscribeSessionListener = null;
      }

      // Solo borra el sessionId de RTDB si aún pertenece a este dispositivo
      // (si otro dispositivo hizo login, el sessionId ya no es nuestro y no lo tocamos)
      const uid = getAuth().currentUser?.uid;
      const localSessionId = localStorage.getItem("sessionId");
      if (uid && localSessionId) {
        const db = getDatabase();
        const snapshot = await get(ref(db, `users/${uid}/sessionId`));
        if (snapshot.val() === localSessionId) {
          await remove(ref(db, `users/${uid}/sessionId`));
        }
      }
      localStorage.removeItem("sessionId");

      await signOut(getAuth());
      context.commit("SET_LOGGED_IN", false);
      context.commit("SET_USER", null);
      context.commit("SET_LEVEL", null);
      context.commit("SET_ROLE", "");
      context.commit("SET_IS_ACTIVE", null);
      context.commit("SET_MY_COMBOS", []);
      context.commit("SET_COMBOS_LOADED", false);
      context.commit("SET_MY_LAB", []);
      context.commit("SET_LAB_LOADED", false);
      context.commit("SET_FIGURES_LOADED", false);
      context.commit("SET_BLACKLIST_LOADED", false);
    },
    async saveOrUpdateCombo(context, { comboId, tricks, comboName }) {
      try {
        const db = getDatabase();
        const uid = getAuth().currentUser?.uid;
        if (!uid) throw new Error("Usuario no autenticado");

        const comboRef = comboId
          ? ref(db, `users/${uid}/combos/${comboId}`) // actualizar
          : ref(db, `users/${uid}/combos/`); // crear nuevo

        if (comboId) {
          // Actualizamos solo los tricks y la fecha de modificación
          await update(comboRef, {
            tricks,
            updated_at: DateTime.now().toISO(),
          });
        } else {
          // Creamos un nuevo combo
          // Obtenemos un nuevo ID basado en el contador de combos
          const snapshot = await get(ref(db, `users/${uid}/combos/`));
          const newId = snapshot.exists()
            ? Object.keys(snapshot.val()).length + 1
            : 1;

          await set(ref(db, `users/${uid}/combos/${newId}`), {
            comboId: newId,
            combo_name: comboName,
            tricks,
            created_at: DateTime.now().toISO(),
          });
        }

        context.commit("SET_COMBOS_LOADED", false);
        return true;
      } catch (err) {
        console.error("Error guardando o actualizando combo:", err);
        return err;
      }
    },

    async saveCombInLab(context, { tricks, experimentName }) {
      if (!tricks || tricks.length === 0 || !experimentName) {
        return false;
      }
      const db = getDatabase();
      const uid = store.getters.getUid || getAuth().currentUser?.uid;
      const experimentData = {
        id: uuidv4(),
        created_at: DateTime.fromISO(DateTime.now()).toString(),
        tricks,
        name: experimentName,
      };
      try {
        await push(ref(db, "users/" + uid + "/lab/experiments/"), experimentData);
        context.commit("SET_LAB_LOADED", false);
        return true;
      } catch (err) {
        console.error("Error al guardar el experimento:", err);
        return false;
      }
    },

    async getComboList(context) {
      if (store.state.combosLoaded) return;
      try {
        const db = getDatabase();
        const uid = getAuth().currentUser?.uid;
        const combosRef = ref(db, "users/" + uid + "/combos/");

        const snapshot = await get(combosRef);
        const data = snapshot.val();
        if (!data) {
          context.commit("SET_MY_COMBOS", []);
        } else {
          const combosArray = Array.isArray(data)
            ? data.filter((n) => n)
            : Object.values(data).filter((n) => n);
          context.commit("SET_COMBOS", combosArray.length);
          context.commit("SET_MY_COMBOS", combosArray);
        }
        context.commit("SET_COMBOS_LOADED", true);
      } catch (error) {
        console.error("Error getting ComboList:", error);
      }
    },

    async getBlacklist(context) {
      const TTL = 60_000; // 60 segundos
      const isStale = Date.now() - store.state.blacklistLoadedAt > TTL;
      if (store.state.blacklistLoaded && !isStale) return;
      try {
        const db = getDatabase();
        const uid = getAuth().currentUser?.uid;
        const snapshot = await get(ref(db, "users/" + uid + "/blacklist/"));
        context.commit("SET_BLACKLIST", snapshot.val() as Record<string, BlacklistItem> | null);
        context.commit("SET_BLACKLIST_LOADED", true);
      } catch (error) {
        console.error("Error getting blacklist:", error);
      }
    },

    async getLabList(context) {
      if (store.state.labLoaded) return;
      const db = getDatabase();
      const uid = store.getters.getUid || getAuth().currentUser?.uid;
      const snapshot = await get(ref(db, "users/" + uid + "/lab/experiments/"));
      const data = snapshot.val();
      if (!data) {
        context.commit("SET_MY_LAB", []);
      } else {
        const labArray = Array.isArray(data) ? data : Object.values(data);
        context.commit("SET_LAB", labArray.length);
        context.commit("SET_MY_LAB", labArray.filter((n) => n));
      }
      context.commit("SET_LAB_LOADED", true);
    },

    async getLabById(context, id) {
      return store.getters.getMyLab.find((exp) => exp.id === id);
    },
    async getComboById(context, comboId) {
      const combos = store.getters.getMyCombos;
      return combos.find((combo: any) => combo.comboId === comboId);
    },
    async loadUserProfile(context, uid: string) {
      try {
        const db = getDatabase();
        const snapshot = await get(ref(db, "users/" + uid));
        const data = snapshot.val();
        if (!data) return;
        context.commit("SET_LEVEL", data.level ?? null);
        context.commit("SET_ROLE", data.role ?? "");
        context.commit("SET_IS_ACTIVE", data.isActive ?? true);
      } catch (error) {
        console.error("Error loading user profile:", error);
      }
    },

    async saveRegisterCode(context, newCode) {
      const db = getDatabase();
      return push(ref(db, "codes/"), {
        code: newCode,
        created_at: DateTime.fromISO(DateTime.now()).toString(),
      })
        .then(() => {
          return true;
        })
        .catch((err) => {
          console.error(err);
          return err;
        });
    },

    async saveFeedback(context, { user, comment }) {
      const db = getDatabase();
      return push(ref(db, "feedback/"), {
        user: user,
        comment: comment,
        created_at: DateTime.fromISO(DateTime.now()).toString(),
      })
        .then(() => {
          return true;
        })
        .catch((err) => {
          console.error(err);
          return err;
        });
    },
    async saveTrick(context, { name, description, family, code, levelName }) {
      const db = getDatabase();
      return push(ref(db, "tricks/"), {
        name: name,
        description: description,
        family: family,
        code: code,
        levelName: levelName,
        created_at: DateTime.fromISO(DateTime.now()).toString(),
      })
        .then(() => {
          return true;
        })
        .catch((err) => {
          console.error(err);
          return err;
        });
    },

    async saveFigure(context, figureData) {
      try {
        const db = getDatabase();
        const uid = getAuth().currentUser?.uid;
        if (!uid) throw new Error("Usuario no autenticado");

        const figure = { ...figureData, created_at: DateTime.now().toISO() };
        await push(ref(db, `users/${uid}/lab/figures/`), figure);
        context.commit("SET_FIGURES_LOADED", false);
        return true;
      } catch (error) {
        console.error("Error al guardar el truco:", error);
        return false;
      }
    },

    async fetchTricks(context) {
      if (store.state.tricksLoaded) return;
      try {
        const db = getDatabase();
        const snapshot = await get(ref(db, "tricks"));
        if (snapshot.exists()) {
          context.commit("SET_TRICKS", snapshot.val());
          context.commit("SET_TRICKS_LOADED", true);
        }
      } catch (error) {
        console.error("No tricks found");
      }
    },

    async fetchFigures(context) {
      if (store.state.figuresLoaded) return;
      try {
        const db = getDatabase();
        const uid = getAuth().currentUser?.uid;
        const snapshot = await get(ref(db, "users/" + uid + "/lab/figures/"));
        if (snapshot.exists()) {
          const tricksArray = Object.entries(snapshot.val()).map(
            ([key, value]) => ({
              id: key,
              ...(typeof value === "object" && value !== null ? value : {}),
            })
          );
          context.commit("SET_FIGURES", tricksArray);
          context.commit("SET_FIGURES_LOADED", true);
        }
      } catch (error) {
        console.error("No figures found");
      }
    },

    async deleteFigure(context, figureId: string) {
      const uid = getAuth().currentUser?.uid;
      if (!uid) throw new Error("Usuario no autenticado");
      await remove(ref(getDatabase(), `users/${uid}/lab/figures/${figureId}`));
      context.commit("SET_FIGURES_LOADED", false);
    },

    async getRegisterCodes(context) {
      const db = getDatabase();
      const snapshot = await get(ref(db, "/codes/"));
      context.commit("SET_CODES", snapshot.val());
    },
  },
});

// export the store
export default store;
